import { map, path, values } from 'ramda'

import { UsersById } from '@driftt/api-types'

import { TideSelectOption } from 'types/tide'
import {
  hasResponseTimerGate,
  hasResponseTimerReportingGate,
} from 'utils/access'
import { createCSV } from 'utils/csv'
import { getHumanizedDurationMilliseconds } from 'utils/format'

import {
  IConversationRep,
  IConversationRepPerformanceExport,
  IConversationTag,
  IConversationURL,
  IUserActivitySummary,
} from '../types'

const ensureNoNan = (value) => (value === 'NaN' ? 0 : value)

export const repPerfChartExport = (
  data: IConversationRepPerformanceExport[],
  usersById: UsersById,
  filename: string
) => {
  const hasResponseTimerReporting =
    hasResponseTimerReportingGate() && hasResponseTimerGate()
  const headers = [
    'Name',
    'Median response time (seconds)',
    'Median conversation length (seconds)',
    ...(hasResponseTimerReporting ? ['Average response time (seconds)'] : []),
    '# of new Conversations',
    'Average rating',
  ]
  const parsedData: any = map((row: IConversationRepPerformanceExport) => {
    const user = usersById[row.user] || {}
    return {
      convoLength: Math.round(ensureNoNan(row.convoLength) / 1000),
      responseTime: Math.round(ensureNoNan(row.responseTime) / 1000),
      avgResponseTime: Math.round(ensureNoNan(row.avgResponseTime) / 1000),
      newConvos: ensureNoNan(row.newConvos),
      liveChatRating: ensureNoNan(row.liveChatRating),
      user: user.name || user.email || 'Your teammate',
    }
  }, data)
  const dataWithHeaders = [
    headers,
    ...map(
      (row) => [
        row.user,
        row.responseTime,
        row.convoLength,
        ...(hasResponseTimerReporting ? [row.avgResponseTime] : []),
        row.newConvos,
        row.liveChatRating,
      ],
      values(parsedData)
    ),
  ]

  createCSV(dataWithHeaders, filename)
}

export const userAvailabilityExport = (
  data: IUserActivitySummary[],
  usersById: UsersById,
  filename: string
) => {
  const headers = [
    'Name',
    'Total Time Online (seconds)',
    'Avg. Time Online per Day (seconds)',
    'Total Time Online (readable)',
    'Avg. Time Online per day (readable)',
  ]
  const parsedData: any = map((row: IUserActivitySummary) => {
    const user = usersById[row.userId] || {}
    return {
      averageTimeReadable: getHumanizedDurationMilliseconds(
        ensureNoNan(row.secondsActivePerDay) * 1000
      ),
      totalTimeReadable: getHumanizedDurationMilliseconds(
        ensureNoNan(row.secondsActive) * 1000
      ),
      totalTime: Math.round(ensureNoNan(row.secondsActive)),
      averageTime: Math.round(ensureNoNan(row.secondsActivePerDay)),
      user: user.name || user.email || 'Your teammate',
    }
  }, data)

  const dataWithHeaders = [
    headers,
    ...map(
      (row) => [
        row.user,
        row.totalTime,
        row.averageTime,
        row.totalTimeReadable,
        row.averageTimeReadable,
      ],
      values(parsedData)
    ),
  ]

  createCSV(dataWithHeaders, filename)
}

export const urlTableExport = (
  data: IConversationURL[],
  _,
  filename: string
) => {
  const headers = ['Conversation URL', '# of new Conversations']
  const dataWithHeaders = [
    headers,
    ...map((row) => [row.url, ensureNoNan(row.value)], data),
  ]

  createCSV(dataWithHeaders, filename)
}

export const topTagsTableExport = (
  data: IConversationTag[],
  _: UsersById,
  filename: string,
  classifications: TideSelectOption[]
) => {
  const headers = ['Tag', '# of new Conversations', '% of new Conversations']
  const toCsvRow = (row: IConversationTag) => [
    path(
      ['label'],
      classifications.find((c) => c.value === row.tag)
    ) || row.tag,
    row.value,
    ((row.value / row.totalCount) * 100).toFixed(1),
  ]
  const dataWithHeaders = [headers, ...map(toCsvRow, data)]

  createCSV(dataWithHeaders, filename)
}

export const repTableExport = (
  data: IConversationRep[],
  usersById: UsersById,
  filename: string
) => {
  const headers = ['Name', '# of new Conversations']
  const parsedData: any = map((row: IConversationRep) => {
    const user = usersById[row.user] || {}
    return {
      value: ensureNoNan(row.value),
      user: user.name || user.email || 'Your teammate',
    }
  }, data)
  const dataWithHeaders = [
    headers,
    ...map((row) => [row.user, row.value], values(parsedData)),
  ]

  createCSV(dataWithHeaders, filename)
}

export const EXPORT_FUNCTON_FOR_METRIC = Object.freeze({
  userAvailability: userAvailabilityExport,
  repPerformance: repPerfChartExport,
  urlTable: urlTableExport,
  repTable: repTableExport,
  topTags: topTagsTableExport,
})
